import React, {Component} from "react";
import {Control, Field, Icon, TextArea} from "bloomer";
import Card from "../../../elements/Card";
import Button from "../../../elements/Button";
import {getTextEmotion} from "../../../api/models/TextEmotion";
import SendGAEvent, {GAEventActions, GAEventModels} from "../../../utils/SendGAEvent";
import getWordsCount from "../../../utils/WordsCount";
import WordsLabel from "../../../elements/WordsLabel";
import ErrorUtils from "../../../utils/ErrorUtils";
import {toast} from "react-toastify";
import SectionName from "../../../elements/SectionName";
import FieldDescription from "../../../elements/FieldDescription";
import InputWarning from "../../../elements/InputWarning";
import Spacer from "../../../elements/Spacer";
import InformationCard from "../../../elements/InformationCard";
import {INVALID_FIELD_STYLE} from "../../../utils/Constants";
import ModelContentContainer from "../../../elements/ModelContentContainer";

interface ITextEmotionModelState {
    loading: boolean;
    textInput: string;
    textInputEmpty: boolean;
    showLimitWarning: boolean;
    result: any;
    wordsCount: number;
    errorMessage: string;
}

class TextEmotionModel extends Component<{}, ITextEmotionModelState> {
    public WORDS_LIMIT: number = 1000;

    public constructor(props: {}) {
        super(props);

        this.state = {
            loading: false,
            textInput: "",
            textInputEmpty: false,
            showLimitWarning: false,
            result: [],
            wordsCount: 0,
            errorMessage: ""
        };
    }

    public componentDidMount() {
        SendGAEvent(GAEventActions.VIEW_MODEL, GAEventModels.TEXT_EMOTION);
    }

    public capitalizeName = (name: string) => {
        return name[0].toUpperCase() + name.substr(1);
    };

    public updateWordsCount = () => {
        const wordsCount = getWordsCount(this.state.textInput.trim());
        this.setState({
            wordsCount,
            showLimitWarning: wordsCount > this.WORDS_LIMIT
        });
    };

    public handleTextInputChange = (e: any) => {
        this.setState({
            textInput: e.target.value,
            textInputEmpty: false,
            showLimitWarning: false
        }, () => {
            this.updateWordsCount();
        });
    };

    public onSubmit = async (e: any) => {
        e.target.blur();

        if (this.state.loading || !this.state.textInput.trim() || this.state.showLimitWarning) {
            this.setState({result: []});

            if (!this.state.textInput.trim()) {
                this.setState({
                    textInputEmpty: true
                });
            }
            return;
        }

        this.setState({
            loading: true,
            result: [],
            errorMessage: ""
        });

        let errorOccurred = false;

        const response = await getTextEmotion(this.state.textInput.trim()).catch((error) => {
            if (ErrorUtils.requestLimitExceeded(error.response)) {
                this.setState({
                    loading: false
                });
                toast.error(ErrorUtils.messageContent.REQUEST_LIMIT_EXCEEDED);
            } else {
                this.setState({
                    loading: false,
                    errorMessage: "Couldn't process text."
                });
            }
            errorOccurred = true;
        });

        if (errorOccurred) {
            return;
        }


        if (response && response.data && response.data.scores && response.data.scores.length) {
            this.setState({
                loading: false,
                result: response.data.scores.sort((a: any, b: any) => {
                    return a.value < b.value ? 1 : -1;
                })
            });
        } else {
            this.setState({
                loading: false,
                errorMessage: "Couldn't process text."
            });
        }

        SendGAEvent(GAEventActions.CALL_MODEL, GAEventModels.TEXT_EMOTION);
    };

    public resetState = async (e: any) => {
        e.target.blur();

        this.setState({
            loading: false,
            textInput: "",
            textInputEmpty: false,
            showLimitWarning: false,
            result: [],
            wordsCount: 0,
            errorMessage: ""
        });
    };

    public render() {
        return <ModelContentContainer>
            <Card>
                <Field>
                    <Control>
                        <SectionName>Text</SectionName>
                        <FieldDescription>Maximum size of input is 1000 words.</FieldDescription>
                        <TextArea
                            style={(this.state.textInputEmpty || this.state.showLimitWarning) ? INVALID_FIELD_STYLE : undefined}
                            onChange={this.handleTextInputChange}
                            onPaste={this.handleTextInputChange}
                            value={this.state.textInput}
                            placeholder={"Enter your text here."}
                        />
                        <div>
                            {
                                this.state.textInputEmpty && <InputWarning>Please input a text.</InputWarning>
                            }
                            {
                                this.state.showLimitWarning &&
                                <InputWarning>Input size limit has been exceeded. Please decrease the
                                    size.</InputWarning>
                            }
                            <WordsLabel>{this.state.wordsCount}&nbsp;{this.state.wordsCount > 1 ? 'words' : 'word'}</WordsLabel>
                        </div>
                    </Control>
                </Field>
                <div>
                    <Button onClick={this.onSubmit}>Submit</Button>
                    <Button onClick={this.resetState} style={{marginLeft: "5px"}}>Reset</Button>
                </div>
                {
                    this.state.loading && <div>
                        <Spacer/>
                        <span>Loading...</span>
                        <Icon className="fas fa-spinner fa-spin"/>
                    </div>
                }
                <Spacer/>
                {
                    !this.state.loading && this.state.result.length ? <div>
                        <SectionName>Output:</SectionName>
                        <InformationCard>
                            {
                                this.state.result.map((score: any) => {
                                    return <p
                                        key={score.name}>{this.capitalizeName(score.name)}{" - "}{score.value}</p>
                                })
                            }
                        </InformationCard>
                    </div> : <></>
                }
                {
                    !this.state.loading && this.state.errorMessage ? <div>
                        <InformationCard>{this.state.errorMessage}</InformationCard>
                    </div> : <></>
                }
            </Card>
        </ModelContentContainer>;
    }
}

export default TextEmotionModel;


// WEBPACK FOOTER //
// ./src/routes/Dashboard/TextEmotion/TextEmotionModel.tsx